import { Link } from "react-router-dom";
import { ArrowRight, Flag, Star, Upload, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import VinLookupCard from "@/components/sections/VinLookupCard";
import { BUSINESS } from "@/lib/constants";

const HERO_IMG = "https://images.unsplash.com/photo-1737488584520-3746b8b1cd83?crop=entropy&cs=srgb&fm=jpg&q=80&w=1800";

export default function HeroClassic() {
  return (
    <section className="relative overflow-hidden bg-brand-navy" data-testid="hero">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="" className="w-full h-full object-cover object-center opacity-70" fetchPriority="high" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/85 to-brand-navy/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-transparent to-brand-navy/40" />
        <div className="absolute inset-0 bg-grain opacity-30" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20 lg:pt-36 lg:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6 text-brand-warm">
            <div className="flex flex-wrap items-center gap-2 reveal reveal-1">
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/15 text-[11px] font-bold uppercase tracking-[0.2em]" data-testid="hero-veteran-badge">
                <Flag className="w-3.5 h-3.5 text-brand-orange" /> Veteran-owned · Hayward, CA
              </span>
              <a href={BUSINESS.yelpUrl} target="_blank" rel="noopener noreferrer" data-testid="hero-yelp-badge" className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-[11px] font-bold uppercase tracking-[0.2em] transition-colors">
                <Star className="w-3.5 h-3.5 fill-current text-[#FF1A1A]" /> {BUSINESS.yelpRating} on Yelp · {BUSINESS.yelpReviews} reviews
              </a>
            </div>

            <h1 className="font-display font-extrabold tracking-[-0.03em] leading-[0.95] text-[clamp(2.6rem,5vw,5rem)] mt-7 reveal reveal-2">
              <span className="whitespace-nowrap">We buy cars.</span><br /><span className="whitespace-nowrap">We pay more.</span><br /><span className="text-brand-blue brightness-150 whitespace-nowrap">Skip the hassle.</span>
            </h1>
            <p className="mt-7 text-lg sm:text-xl text-blue-100/85 leading-relaxed max-w-lg reveal reveal-3">
              Enter your VIN, we inspect in person, and you get a real written offer — with same-day payment the moment you accept.
            </p>

            <dl className="mt-10 grid grid-cols-3 gap-6 max-w-lg border-t border-white/15 pt-6 reveal reveal-4" data-testid="hero-benefits">
              {[["3,000+", "cars bought"], ["25+ years", "in the business"], ["Same-day", "payment"]].map(([v, l]) => (
                <div key={l}><dt className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">{v}</dt><dd className="text-[11px] uppercase tracking-[0.2em] text-blue-200/70 font-bold mt-1">{l}</dd></div>
              ))}
            </dl>

            <p className="mt-8 text-sm text-blue-200/70 reveal reveal-4">
              Prefer to talk? <a href={BUSINESS.phoneHref} className="text-brand-warm font-semibold hover:underline">Call</a> or <a href={BUSINESS.smsHref} data-testid="hero-sms-link" className="text-brand-warm font-semibold hover:underline">text</a> {BUSINESS.phone}
            </p>
          </div>

          <div className="lg:col-span-6 w-full space-y-4 reveal reveal-3">
            <div className="relative">
              <div className="absolute -inset-1 rounded-[26px] bg-gradient-to-br from-brand-blue/50 via-transparent to-brand-orange/30 blur-xl opacity-70" aria-hidden />
              <div className="relative">
                <VinLookupCard variant="hero" />
              </div>
            </div>
            <Link to="/sell-your-car" state={{ beatOffer: true }} data-testid="hero-beat-offer" className="group flex items-center gap-4 rounded-2xl bg-white/[0.07] hover:bg-white/[0.12] border border-white/15 px-5 py-4 transition-colors">
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-brand-orange/20 text-brand-orange flex-shrink-0"><Upload className="w-5 h-5" /></span>
              <span className="flex-1 min-w-0">
                <span className="block font-display font-bold text-brand-warm">Have a CarMax or Carvana offer?</span>
                <span className="block text-sm text-blue-200/75">Upload it and give us a chance to beat it. No obligation.</span>
              </span>
              <ArrowRight className="w-5 h-5 text-brand-warm/70 group-hover:translate-x-1 transition-transform" />
            </Link>
            <p className="text-[11px] text-blue-200/60 flex items-center gap-1.5 px-1"><ShieldCheck className="w-3.5 h-3.5" /> Written offer after inspection. Beating another offer is not guaranteed.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
